import React from "react";
import "./stockDialog.scss";
import { useState, useEffect } from "react";
import UKToast from "@/components/UKToast";
import UKDialog from "@/components/UKDialog";
import { useTranslation } from "@/i18n/client";
import CloseIcon from "@mui/icons-material/Close";
import RedClose from "@/assets/icons/redClose.svg";
import OutOfStackController from "./outofStockController";
import ContactInfoValidUtil from "@/utils/contactInfoValidTool";
import { outOfStockNotify } from "@/apis/outofStock/outofStock.api";
import { Button, DialogContent, DialogTitle, FormHelperText, IconButton, OutlinedInput } from "@mui/material";

interface OutOfStockProps {
  open: boolean;
  email: string;
  skuIds: string[];
  isLoggedIn: boolean;
  onSuccess?: () => void;
  onFail?: () => void;
  closeFn: () => void;
}

const ContinueBtnStyle = {
  textTransform: "none",
  height: "32px",
  fontSize: "14px",
  lineHeight: "18px",
  color: "#FFFFFF",
  background: "#CC0C1C",
  border: "1px solid #E0E1E5",
  "&.Mui-disabled": {
    backgroundColor: "#FF9FA7",
    color: "#FFFFFF",
  },
}

const getContactInputStyle = (error: boolean) => {
  return {
    width: "100%",
    fontSize: "16px",
    lineHeight: "20px",
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: error ? "#FF0F23": "#1A1A1A",
      borderWidth: 2,
    },
    '& .MuiOutlinedInput-input::placeholder': {
      color: "#505259",
      opacity: 1,
    },
    '& .MuiOutlinedInput-input': {
      height: "auto",
      padding: '10px 12px',
    },
    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: "#FF0F23",
    },
    '&.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: "#FF0F23",
      borderWidth: 2,
    },
  }
}

const OutOfStockDialog = ({ open, skuIds, email, isLoggedIn, onSuccess, onFail, closeFn }: OutOfStockProps) => {

  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isFail, setIsFail] = useState(false);
  const [emailValue, setEmailValue] = useState<string>(email || "");
  const [helperTextError, setHelperTextError] = useState<string>("");

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  const handleClose = () => {
    setIsOpen(false);
    closeFn && closeFn();
    isFail && onFail && onFail();
  };

  const handleEmailValueChange = (value: string) => {
    if (value) {
      const isValid = ContactInfoValidUtil.isEmailPattern(value);
      setHelperTextError(isValid ? "" : t("outofstock.helpTextError"));
    }
    else {
      setHelperTextError("");
    }
    setEmailValue(value);
  };

  const sendNotify = async () => {
    const res = await outOfStockNotify(emailValue, skuIds);
    if(res && res.data){
      setIsFail(false);
      handleClose();
      openSuccessDialog();
    }
    else {
      setIsFail(true);
      if(res?.msg){
        UKToast.show({
          icon: "error",
          content: res?.msg,
        })
      }
    }
  };

  const openSuccessDialog = () => {
    let dialogBtnParam = null;
    if(isLoggedIn){
      const showBind = !email || email.length === 0;
      const showVerify = !!email && emailValue === email;
      dialogBtnParam = {
        isShowLogin: false,
        isShowBind: showBind,
        isShowVerify: showVerify,
        isShowCancel: showBind || showVerify,
      }
    }
    else{
      dialogBtnParam = {
        isShowLogin: true,
        isShowBind: false,
        isShowVerify: false,
        isShowCancel: true,
      }
    }
    const params = {
      onSuccess, 
      email: emailValue,
      ...dialogBtnParam,
    }
    OutOfStackController.getInstance().openSignUpSuccessDialog(params);
  };

  function SubscribeContent() {
    return ( <>
      <DialogTitle 
        id="customized-dialog-title"
        sx={{ 
          margin: 0,
          fontSize: "16px",
          fontWeight: "700",
          lineHeight: "20px",
          padding: "20px 24px",
        }}
      >
        <div>{t("outofstock.title")}</div>
        <div className="title-desc">{t("outofstock.titleDesc")}</div>
      </DialogTitle>
      <DialogContent dividers sx={{ borderBottomColor: "#00000000", borderTopColor: "#00000000", padding: "24px", paddingTop: "8px" }}>
        <div className="tell-email">{t("outofstock.tellEmail")}</div>
          <OutlinedInput
            type="text"
            value={emailValue}
            error={!!helperTextError}
            sx={getContactInputStyle(!!helperTextError)}
            placeholder={t("outofstock.enterEmail")}
            inputProps={{ maxLength: 50 }}
            onChange={(event) => handleEmailValueChange(event.target.value)}
          />
          {!!helperTextError && (
            <FormHelperText>
              <span className="error-text">
                <RedClose />
                {helperTextError}
              </span>
            </FormHelperText>
          )}
        <div className="privacy-notice">
          {t("outofstock.privacyNotice")}
          <a href="/help/privacy-policy" target="_blank">{t("outofstock.privacyNoticeBtn")}</a>
        </div>
        <div className="dialog-footer">
          <Button sx={ContinueBtnStyle} onClick={() => {sendNotify()}} disableElevation disabled={!!helperTextError || !emailValue}>{t("login.common.continue")}</Button>
        </div>
      </DialogContent>
    </> )
  }

  return (
    <UKDialog
      open={isOpen}
      showCloseBtn={false}
      onClose={handleClose}
      sx={{
        "& .MuiDialog-paper":{
          width: "592px",
          minHeight: "334px",
          borderRadius: "12px",
        }
      }}
    >
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={(theme) => ({
          position: "absolute",
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      {SubscribeContent()}
    </UKDialog>
  );
};

export default OutOfStockDialog;
